import * as React from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Snackbar,
    Stack,
    Typography,
} from '@mui/material';
import {
    getUserPrivateClub,
    getUserPrivateClubCurrentRoundMatches,
    getUserPrivateClubGrabbedBets,
    getUserPrivateClubLeaderboard,
    getUserPrivateClubPlacedBetsForMatch,
    placeUserPrivateClubPick,
} from '../Api';
import PrivateClubMatchSummary from './PrivateClubMatchSummary';
import PrivateClubDto, {
    PrivateClubBetDto,
    PrivateClubBetSectionDto,
    PrivateClubLeaderboardEntryDto,
    PrivateClubPlacedBetDto,
    PrivateClubTemplateDto,
} from '../models/dtos/PrivateClubDto';
import {
    PrivateClubCurrentRoundAdminDto,
    PrivateClubCurrentRoundMatchAdminDto,
} from '../models/dtos/PrivateClubAdminDto';

const privateClubBetGroupFallbackNames: Record<number, string> = {
    1: '1x2',
    5: 'Total over',
    6: 'Total under',
    7: 'Both teams to score',
    46: 'Player to score',
    47: 'Player not to score',
};

function formatDate(value?: string | null) {
    return value ? new Date(value).toLocaleString() : '-';
}

function formatBetcoins(value: number) {
    return value.toLocaleString();
}

function totalPlayableMatches(template?: PrivateClubTemplateDto) {
    return template?.rounds.reduce((total, round) => {
        return total + round.matches.filter(match => match.settlementStatus !== 'EXCLUDED').length;
    }, 0) ?? 0;
}

function betDisplayName(bet: PrivateClubBetDto) {
    return bet.bet_name?.trim() || 'Bet';
}

function placedPickBetName(pick: PrivateClubPlacedBetDto) {
    return pick.betName?.trim() || `Bet type ${pick.betTypeId} (${roundedBetTypeParam(pick.betTypeParam)})`;
}

function placedPickGroupName(pick: PrivateClubPlacedBetDto) {
    if (pick.betGroupName?.trim()) return pick.betGroupName;
    if (pick.betTypeGroupId != null) return privateClubBetGroupFallbackNames[pick.betTypeGroupId] ?? `Group ${pick.betTypeGroupId}`;
    return 'Bet group';
}

function roundedBetTypeParam(value: number) {
    return Math.round(value * 100) / 100;
}

function placedPickMatchesBet(pick: PrivateClubPlacedBetDto, bet: PrivateClubBetDto, betTypeGroupId: number) {
    const groupMatches = pick.betTypeGroupId == null || pick.betTypeGroupId === betTypeGroupId;
    return groupMatches
        && pick.betTypeId === bet.bet_type_id
        && roundedBetTypeParam(pick.betTypeParam) === roundedBetTypeParam(bet.bet_type_param);
}

function placedPickForGroup(
    placedPicks: PrivateClubPlacedBetDto[],
    groupId: number,
    bets: PrivateClubBetDto[],
) {
    return placedPicks.find(pick => {
        return pick.betTypeGroupId === groupId || bets.some(bet => placedPickMatchesBet(pick, bet, groupId));
    });
}

function pickKey(roundId: string, matchId: number) {
    return `${roundId}:${matchId}`;
}

function groupPickKey(roundId: string, matchId: number, betTypeGroupId: number) {
    return `${roundId}:${matchId}:${betTypeGroupId}`;
}

interface PrivateClubTestAsUserDialogProps {
    open: boolean;
    userId: number;
    club: PrivateClubDto | null;
    templates: PrivateClubTemplateDto[];
    onClose: () => void;
    onClubUpdated?: (club: PrivateClubDto) => void;
}

export default function PrivateClubTestAsUserDialog(props: PrivateClubTestAsUserDialogProps) {
    const [selectedClub, setSelectedClub] = React.useState<PrivateClubDto | null>(null);
    const [grabbedBetSectionsByKey, setGrabbedBetSectionsByKey] = React.useState<Record<string, PrivateClubBetSectionDto[]>>({});
    const [selectedGrabbedBetByKey, setSelectedGrabbedBetByKey] = React.useState<Record<string, string>>({});
    const [currentRound, setCurrentRound] = React.useState<PrivateClubCurrentRoundAdminDto | null>(null);
    const [currentRoundMatches, setCurrentRoundMatches] = React.useState<PrivateClubCurrentRoundMatchAdminDto[]>([]);
    const [leaderboard, setLeaderboard] = React.useState<PrivateClubLeaderboardEntryDto[]>([]);
    const [placedBetsByKey, setPlacedBetsByKey] = React.useState<Record<string, PrivateClubPlacedBetDto[]>>({});
    const [currentRoundLoading, setCurrentRoundLoading] = React.useState(false);
    const [actionLoadingKey, setActionLoadingKey] = React.useState<string | null>(null);
    const [error, setError] = React.useState<string | null>(null);
    const [message, setMessage] = React.useState<string | null>(null);
    const [toastMessage, setToastMessage] = React.useState<string | null>(null);

    const templateMap = React.useMemo(() => {
        return new Map(props.templates.map(template => [template.id, template]));
    }, [props.templates]);
    const propsClubId = props.club?.id ?? null;
    const propsClubRef = React.useRef<PrivateClubDto | null>(null);
    propsClubRef.current = props.club;
    const selectedClubId = selectedClub?.id ?? null;

    React.useEffect(() => {
        const initialClub = propsClubRef.current;
        if (!props.open || !initialClub) {
            setSelectedClub(null);
            return;
        }

        let cancelled = false;
        setError(null);
        setMessage(null);
        setToastMessage(null);
        setGrabbedBetSectionsByKey({});
        setSelectedGrabbedBetByKey({});
        setCurrentRound(null);
        setCurrentRoundMatches([]);
        setLeaderboard([]);
        setPlacedBetsByKey({});
        setSelectedClub(initialClub);
        getUserPrivateClub(props.userId, initialClub.id).then(loadedClub => {
            if (cancelled) return;
            setSelectedClub(loadedClub ?? initialClub);
            if (!loadedClub) setError('Failed to load private club details.');
        });

        return () => { cancelled = true; };
    }, [propsClubId, props.open, props.userId]);

    React.useEffect(() => {
        if (!props.open || !selectedClubId) return;

        let cancelled = false;
        setCurrentRoundLoading(true);
        Promise.all([
            getUserPrivateClubCurrentRoundMatches(props.userId, selectedClubId),
            getUserPrivateClubLeaderboard(props.userId, selectedClubId),
        ]).then(([roundMatchesResponse, leaderboardResponse]) => {
            if (cancelled) return;
            setCurrentRound(roundMatchesResponse?.round ?? null);
            setCurrentRoundMatches(roundMatchesResponse?.matches ?? []);
            setLeaderboard(leaderboardResponse?.items ?? []);
            if (!roundMatchesResponse) setError(prev => prev ?? 'Failed to load current round matches.');
            if (!leaderboardResponse) setError(prev => prev ?? 'Failed to load private club leaderboard.');
        }).finally(() => {
            if (!cancelled) setCurrentRoundLoading(false);
        });

        return () => { cancelled = true; };
    }, [props.open, props.userId, selectedClubId]);

    React.useEffect(() => {
        if (!props.open || !selectedClubId || !currentRound || currentRoundMatches.length === 0) {
            setPlacedBetsByKey({});
            return;
        }

        let cancelled = false;
        Promise.all(currentRoundMatches.map(item => {
            const key = pickKey(currentRound.id, item.matchId);
            return getUserPrivateClubPlacedBetsForMatch(props.userId, selectedClubId, currentRound.id, item.matchId)
                .then(response => [key, response?.placed_bets ?? []] as const)
                .catch(() => [key, [] as PrivateClubPlacedBetDto[]] as const);
        })).then(results => {
            if (cancelled) return;
            setPlacedBetsByKey(prev => {
                const next = { ...prev };
                results.forEach(([key, items]) => {
                    next[key] = items;
                });
                return next;
            });
        });

        return () => { cancelled = true; };
    }, [currentRound, currentRoundMatches, props.open, props.userId, selectedClubId]);

    const loadGrabbedBets = async (
        club: PrivateClubDto,
        round: PrivateClubCurrentRoundAdminDto,
        match: PrivateClubCurrentRoundMatchAdminDto,
    ) => {
        const key = pickKey(round.id, match.matchId);
        setActionLoadingKey(`load:${key}`);
        setError(null);
        const result = await getUserPrivateClubGrabbedBets(props.userId, club.id, round.id, match.matchId);
        setActionLoadingKey(null);
        if (!result) {
            setError('Failed to load grabbed bets for this round match.');
            return;
        }
        setGrabbedBetSectionsByKey(prev => ({ ...prev, [key]: result.sections }));
    };

    const refreshPlacedBetsForMatch = async (
        club: PrivateClubDto,
        round: PrivateClubCurrentRoundAdminDto,
        match: PrivateClubCurrentRoundMatchAdminDto,
        fallbackPlaced: PrivateClubPlacedBetDto[],
    ) => {
        const key = pickKey(round.id, match.matchId);
        const response = await getUserPrivateClubPlacedBetsForMatch(props.userId, club.id, round.id, match.matchId);
        const picks = response?.placed_bets ?? fallbackPlaced;
        setPlacedBetsByKey(prev => ({ ...prev, [key]: picks }));
        setCurrentRoundMatches(prev => prev.map(item => (
            item.matchId === match.matchId
                ? { ...item, match_info: response?.sport_match ?? item.match_info, placedBetsCount: picks.length }
                : item
        )));
        const refreshedClub = await getUserPrivateClub(props.userId, club.id);
        if (refreshedClub) {
            setSelectedClub(refreshedClub);
            props.onClubUpdated?.(refreshedClub);
        }
    };

    const placePicks = async (
        club: PrivateClubDto,
        round: PrivateClubCurrentRoundAdminDto,
        match: PrivateClubCurrentRoundMatchAdminDto,
        grabbedBetIds: number[],
    ) => {
        const key = pickKey(round.id, match.matchId);
        if (grabbedBetIds.length === 0) {
            setError('At least one grabbed bet is required.');
            return;
        }
        setActionLoadingKey(`pick:${key}`);
        setError(null);
        setMessage(null);
        const result = await placeUserPrivateClubPick(props.userId, club.id, {
            roundId: round.id,
            matchId: match.matchId,
            grabbedBetIds,
        });
        if (!result.ok) {
            setActionLoadingKey(null);
            setError(result.error ?? 'Private club pick was not placed.');
            return;
        }
        const placed = result.data ?? [];
        setActionLoadingKey(null);
        setMessage(grabbedBetIds.length === 1 ? 'Private club pick placed.' : 'Private club picks placed.');
        await refreshPlacedBetsForMatch(club, round, match, [...(placedBetsByKey[key] ?? []), ...placed]);
    };

    const titleUserName = leaderboard.find(entry => entry.user.id === props.userId)?.user.name?.trim() || `User ${props.userId}`;

    return (
        <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="lg">
            <DialogTitle>
                Test private club as{' '}
                <Box
                    component="a"
                    href={`/users/${props.userId}`}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                    {titleUserName}
                </Box>
            </DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
                {message && <Alert severity="success" sx={{ mb: 1 }}>{message}</Alert>}
                {selectedClub && (() => {
                    const template = templateMap.get(selectedClub.templateId);
                    return (
                        <Stack gap={2} sx={{ mt: 1 }}>
                            <Stack gap={0.5}>
                                <Typography variant="subtitle1">{selectedClub.name}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Template: {template?.title ?? selectedClub.templateId} | Status: {selectedClub.status}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Members: {selectedClub.membersCount} | Placed picks: {selectedClub.placedBetsCount} / {totalPlayableMatches(template)}
                                </Typography>
                                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                                    <Chip
                                        size="small"
                                        variant="outlined"
                                        label={selectedClub.rewardMode}
                                        color={selectedClub.rewardMode === 'BETCOIN' ? 'primary' : 'default'}
                                    />
                                    {selectedClub.rewardMode === 'BETCOIN' && (
                                        <Typography variant="body2" color="text.secondary">
                                            Entry fee: {formatBetcoins(selectedClub.entryFeeBetcoins ?? 0)} betcoins | Bank: {formatBetcoins(selectedClub.bankBetcoins)} betcoins
                                        </Typography>
                                    )}
                                </Stack>
                            </Stack>
                            <Divider />
                            {!template && <Alert severity="warning">Template data is not loaded for this club.</Alert>}
                            {currentRoundLoading && <Typography variant="body2" color="text.secondary">Loading current round...</Typography>}
                            {!currentRoundLoading && !currentRound && (
                                <Alert severity="info">There is no current round with available matches.</Alert>
                            )}
                            {currentRound && (
                                <Stack key={currentRound.id} gap={1} sx={{ border: '1px solid #ddd', borderRadius: 1, p: 1.5 }}>
                                    <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                                        <Typography variant="subtitle2">Round {currentRound.index}</Typography>
                                        <Chip size="small" label={currentRound.status} />
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDate(currentRound.startsAt)} - {formatDate(currentRound.endsAt)}
                                        </Typography>
                                    </Stack>
                                    {currentRoundMatches.length === 0 && <Typography variant="body2" color="text.secondary">No matches.</Typography>}
                                    {currentRoundMatches.map(item => {
                                        const match = item;
                                        const key = pickKey(currentRound.id, match.matchId);
                                        const sections = grabbedBetSectionsByKey[key];
                                        const betsCount = sections?.reduce((count, section) => (
                                            count + section.groups.reduce((groupCount, group) => groupCount + group.bets.length, 0)
                                        ), 0) ?? 0;
                                        const allowedGroupNames = match.allowedBetTypeGroupIds
                                            .map(groupId => privateClubBetGroupFallbackNames[groupId] ?? null)
                                            .filter(Boolean)
                                            .join(', ');
                                        const loadingBets = actionLoadingKey === `load:${key}`;
                                        const placingPick = actionLoadingKey === `pick:${key}`;
                                        const placedPicks = placedBetsByKey[key] ?? [];
                                        const maxPicksPerMatch = template?.maxPicksPerMatch ?? 2;
                                        const selectedGrabbedBetIds = sections
                                            ?.flatMap(section => section.groups)
                                            .filter(group => !placedPickForGroup(placedPicks, group.group.id, group.bets))
                                            .map(group => Number(selectedGrabbedBetByKey[groupPickKey(currentRound.id, match.matchId, group.group.id)]))
                                            .filter(id => Number.isFinite(id) && id > 0)
                                            .slice(0, Math.max(0, maxPicksPerMatch - placedPicks.length)) ?? [];
                                        const limitReached = placedPicks.length >= maxPicksPerMatch;
                                        return (
                                            <Stack key={key} gap={1} sx={{ borderTop: '1px solid #eee', pt: 1 }}>
                                                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ minWidth: 0 }}>
                                                    <PrivateClubMatchSummary matchId={match.matchId} match={item.match_info ?? null} link />
                                                    <Chip
                                                        size="small"
                                                        label={item.settlementStatus}
                                                        color={item.settlementStatus === 'SETTLED' ? 'success' : item.settlementStatus === 'PENDING' ? 'warning' : 'default'}
                                                    />
                                                    {placedPicks.length > 0 && <Chip size="small" label={`${placedPicks.length}/${maxPicksPerMatch} picked`} color="success" />}
                                                </Stack>
                                                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap" sx={{ pl: { xs: 0, sm: 4.5 } }}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Allowed groups: {allowedGroupNames || '-'}
                                                    </Typography>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => loadGrabbedBets(selectedClub, currentRound, match)}
                                                        disabled={loadingBets || item.settlementStatus === 'EXCLUDED'}
                                                    >
                                                        {loadingBets ? 'Loading...' : 'Show bets'}
                                                    </Button>
                                                </Stack>
                                                {placedPicks.length > 0 && (
                                                    <Stack gap={0.75} sx={{ pl: { xs: 0, sm: 4.5 } }}>
                                                        <Typography variant="caption" color="text.secondary">Placed picks</Typography>
                                                        <Stack direction="row" gap={1} flexWrap="wrap">
                                                            {placedPicks.map(pick => (
                                                                <Chip
                                                                    key={pick.externalId}
                                                                    size="small"
                                                                    color="success"
                                                                    variant="outlined"
                                                                    label={`${placedPickGroupName(pick)}: ${placedPickBetName(pick)} @ ${pick.oddsSnapshot} | ${pick.scorePointsReceived}/${pick.scorePoints} pts | ${pick.resultState}`}
                                                                />
                                                            ))}
                                                        </Stack>
                                                    </Stack>
                                                )}
                                                {sections && (
                                                    <Stack gap={1}>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {betsCount} allowed bets
                                                        </Typography>
                                                        {betsCount === 0 && (
                                                            <Typography variant="body2" color="text.secondary">No allowed bets</Typography>
                                                        )}
                                                        {sections.map(section => section.groups.map(group => (
                                                            <Stack key={`${section.variety.id}:${group.group.id}`} gap={0.75}>
                                                                <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                                                                    <Typography variant="caption" color="text.secondary">
                                                                        {group.group.name || privateClubBetGroupFallbackNames[group.group.id] || 'Bet group'}
                                                                    </Typography>
                                                                    {placedPickForGroup(placedPicks, group.group.id, group.bets) && (
                                                                        <Chip size="small" label="Picked" color="success" />
                                                                    )}
                                                                </Stack>
                                                                <Stack direction="row" gap={1} flexWrap="wrap">
                                                                    {group.bets.map(bet => {
                                                                        const betId = String(bet.grabbed_bet_id);
                                                                        const groupKey = groupPickKey(currentRound.id, match.matchId, group.group.id);
                                                                        const selected = selectedGrabbedBetByKey[groupKey] === betId;
                                                                        const placedPick = placedPicks.find(pick => placedPickMatchesBet(pick, bet, group.group.id));
                                                                        const placedSelected = Boolean(placedPick);
                                                                        const groupAlreadyPicked = Boolean(placedPickForGroup(placedPicks, group.group.id, group.bets));
                                                                        const selectedGroupsCount = sections
                                                                            .flatMap(sectionItem => sectionItem.groups)
                                                                            .filter(groupItem => !placedPickForGroup(placedPicks, groupItem.group.id, groupItem.bets))
                                                                            .filter(groupItem => {
                                                                                const selectedBetId = selectedGrabbedBetByKey[
                                                                                    groupPickKey(currentRound.id, match.matchId, groupItem.group.id)
                                                                                ];
                                                                                return selectedBetId != null && selectedBetId.length > 0;
                                                                            }).length;
                                                                        const maxSelectableGroupsReached = placedPicks.length + selectedGroupsCount >= maxPicksPerMatch;
                                                                        return (
                                                                            <Button
                                                                                key={bet.grabbed_bet_id}
                                                                                size="small"
                                                                                variant={selected || placedSelected ? 'contained' : 'outlined'}
                                                                                color={selected ? 'primary' : 'inherit'}
                                                                                disabled={!bet.is_available && !placedSelected}
                                                                                onClick={() => {
                                                                                    if (groupAlreadyPicked) {
                                                                                        return;
                                                                                    }
                                                                                    if (selected) {
                                                                                        setSelectedGrabbedBetByKey(prev => {
                                                                                            const next = { ...prev };
                                                                                            delete next[groupKey];
                                                                                            return next;
                                                                                        });
                                                                                        return;
                                                                                    }
                                                                                    if (!selectedGrabbedBetByKey[groupKey] && maxSelectableGroupsReached) {
                                                                                        setToastMessage(`Only ${maxPicksPerMatch} picks are allowed for this match.`);
                                                                                        return;
                                                                                    }
                                                                                    setSelectedGrabbedBetByKey(prev => ({ ...prev, [groupKey]: betId }));
                                                                                    }}
                                                                                sx={{
                                                                                    justifyContent: 'stretch',
                                                                                    minWidth: 210,
                                                                                    p: 0,
                                                                                    overflow: 'hidden',
                                                                                    textTransform: 'none',
                                                                                }}
                                                                            >
                                                                                <Box
                                                                                    component="span"
                                                                                    sx={{
                                                                                        flex: 1,
                                                                                        px: 1,
                                                                                        py: 0.5,
                                                                                        minWidth: 0,
                                                                                        overflow: 'hidden',
                                                                                        textOverflow: 'ellipsis',
                                                                                        whiteSpace: 'nowrap',
                                                                                        bgcolor: placedSelected ? 'warning.main' : selected ? 'primary.main' : 'action.hover',
                                                                                        color: selected || placedSelected ? 'common.white' : 'text.primary',
                                                                                    }}
                                                                                >
                                                                                    {betDisplayName(bet)}
                                                                                </Box>
                                                                                <Box
                                                                                    component="span"
                                                                                    sx={{
                                                                                        px: 1,
                                                                                        py: 0.5,
                                                                                        display: 'inline-flex',
                                                                                        gap: 0.75,
                                                                                        alignItems: 'center',
                                                                                        borderLeft: 1,
                                                                                        borderColor: placedSelected ? 'warning.dark' : selected ? 'primary.dark' : 'divider',
                                                                                        bgcolor: placedSelected ? 'warning.dark' : selected ? 'primary.dark' : 'background.paper',
                                                                                        color: selected || placedSelected ? 'common.white' : 'text.primary',
                                                                                        whiteSpace: 'nowrap',
                                                                                    }}
                                                                                >
                                                                                    <Box component="span">{bet.grabbed_coeficient}</Box>
                                                                                    <Box component="span" sx={{ fontWeight: 700 }}>{bet.score_points} pts</Box>
                                                                                </Box>
                                                                            </Button>
                                                                        );
                                                                    })}
                                                                </Stack>
                                                            </Stack>
                                                        )))}
                                                        <Stack direction="row" justifyContent="flex-end">
                                                            <Button
                                                                size="small"
                                                                variant="contained"
                                                                onClick={() => placePicks(selectedClub, currentRound, match, selectedGrabbedBetIds)}
                                                                disabled={placingPick || selectedGrabbedBetIds.length === 0 || limitReached}
                                                            >
                                                                {placingPick ? 'Placing...' : selectedGrabbedBetIds.length > 1 ? `Place ${selectedGrabbedBetIds.length} picks` : 'Place pick'}
                                                            </Button>
                                                        </Stack>
                                                    </Stack>
                                                )}
                                            </Stack>
                                        );
                                    })}
                                </Stack>
                            )}
                            {leaderboard.length > 0 && (
                                <Stack gap={0.75}>
                                    <Typography variant="subtitle2">Leaderboard</Typography>
                                    <Stack direction="row" gap={1} flexWrap="wrap">
                                        {leaderboard.slice(0, 5).map(entry => (
                                            <Chip
                                                key={`${entry.rank}:${entry.user.id}`}
                                                size="small"
                                                variant={entry.user.id === props.userId ? 'filled' : 'outlined'}
                                                color={entry.user.id === props.userId ? 'primary' : 'default'}
                                                label={`#${entry.rank} ${(entry.user.name?.trim() || entry.user.id)}: ${entry.totalPoints} pts`}
                                            />
                                        ))}
                                    </Stack>
                                </Stack>
                            )}
                        </Stack>
                    );
                })()}
            </DialogContent>
            <DialogActions>
                <Button onClick={props.onClose}>Close</Button>
            </DialogActions>
            <Snackbar
                open={toastMessage != null}
                autoHideDuration={3500}
                onClose={() => setToastMessage(null)}
                message={toastMessage ?? ''}
            />
        </Dialog>
    );
}
